<template>
  <div class="teacher-profile">
    <!-- 个人信息卡片 -->
    <el-card class="profile-card">
      <div slot="header" class="card-header">
        <span>个人信息</span>
        <el-button
          type="primary"
          size="small"
          class="edit-btn"
          @click="toggleEdit"
        >
          {{ isEditing ? '取消' : '编辑' }}
        </el-button>
      </div>
      
      <!-- 添加欢迎提示 -->
      <div class="welcome-message">
        欢迎回来，{{ teacherInfo.name || 'teacher' }}！
      </div>
      
      <el-form
        ref="profileForm"
        :model="teacherInfo"
        :rules="profileRules"
        label-width="120px"
        class="profile-form"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="教师ID" prop="teacherId">
              <el-input v-model="teacherInfo.teacherId" disabled placeholder="请输入教师ID"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="姓名" prop="name">
              <el-input v-model="teacherInfo.name" :disabled="!isEditing" placeholder="请输入姓名"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="性别" prop="gender">
              <el-select v-model="teacherInfo.gender" :disabled="!isEditing" placeholder="请选择性别">
                <el-option label="男" value="男"></el-option>
                <el-option label="女" value="女"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="教学年级" prop="teachingGrade">
              <el-input v-model="teacherInfo.teachingGrade" :disabled="!isEditing" placeholder="请输入教学年级"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="教学班级" prop="teachingClass">
              <el-input v-model="teacherInfo.teachingClass" :disabled="!isEditing" placeholder="请输入教学班级"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系电话" prop="phone">
              <el-input v-model="teacherInfo.phone" :disabled="!isEditing" placeholder="请输入联系电话"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="电子邮箱" prop="email">
              <el-input v-model="teacherInfo.email" :disabled="!isEditing" placeholder="请输入电子邮箱"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="所属学院">
              <el-input v-model="teacherInfo.college" :disabled="!isEditing" placeholder="请输入学院"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-form-item v-if="isEditing" class="form-actions">
          <el-button type="primary" @click="submitForm">提交</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, getCurrentInstance } from 'vue';
import { ElMessage } from 'element-plus';
import request from '@/utils/request';

const { proxy } = getCurrentInstance();

// 编辑状态
const isEditing = ref(false);

// 教师信息
const teacherInfo = reactive({
  teacherId: '',
  name: '',
  gender: '',
  teachingGrade: '',
  teachingClass: '',
  phone: '',
  email: '',
  college: ''
});

// 表单验证规则
const profileRules = {
  teacherId: [
    { required: true, message: '请输入教师ID', trigger: 'blur' }
  ],
  name: [
    { required: true, message: '请输入姓名', trigger: 'blur' }
  ],
  gender: [
    { required: true, message: '请选择性别', trigger: 'change' }
  ],
  teachingGrade: [
    { required: true, message: '请输入教学年级', trigger: 'blur' }
  ],
  teachingClass: [
    { required: true, message: '请输入教学班级', trigger: 'blur' }
  ],
  phone: [
    { required: true, message: '请输入联系电话', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
  ],
  email: [
    { required: true, message: '请输入电子邮箱', trigger: 'blur' },
    { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
  ]
};

// 切换编辑状态
const toggleEdit = () => {
  isEditing.value = !isEditing.value;
};

// 提交表单
const submitForm = async () => {
  try {
    const form = proxy.$refs.profileForm;
    await form.validate();

    console.log('Sending request to /teacher/update-profile with data:', teacherInfo);
    // 转换数据格式，确保与后端实体类字段匹配
    const requestData = {
      // 不再传递teacherId，后端会从token中自动解析并设置
      name: teacherInfo.name,
      gender: teacherInfo.gender,
      teachingGrade: teacherInfo.teachingGrade,
      teachingClass: teacherInfo.teachingClass,
      email: teacherInfo.email,
      // 只使用phoneNumber和contactInfo，不发送phone字段
      phoneNumber: teacherInfo.phone,
      contactInfo: teacherInfo.phone
    };
    console.log('实际发送的数据:', requestData);
    const response = await request.post('/teacher/update-profile', requestData);
    console.log('Received response:', response);
    console.log('Response type:', typeof response);
    console.log('Response data:', response?.data);
    // 根据响应拦截器的实现，我们需要从response.data中获取success标志
    if (response && response.data && response.data.success) {
      ElMessage.success('信息更新成功');
      isEditing.value = false;
    } else {
      ElMessage.error('信息更新失败');
      console.error('更新失败原因：response.data.success 为', response?.data?.success);
    }
  } catch (error) {
    console.error('提交表单失败:', error);
    ElMessage.error('提交表单失败，请重试');
  }
};



// 获取教师信息
const fetchTeacherInfo = async () => {
  try {
    // 检查localStorage中是否有token，只有在有token的情况下才发送请求
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('没有token，不发送教师信息请求');
      return;
    }
    
    console.log('发送教师信息请求，使用token自动识别身份');
    
    // 不再传递teacherId参数，后端会从token中自动解析
    const teacherData = await request.get('/teacher/profile');
    
    console.log('获取教师信息成功:', teacherData);
    // 注意：根据响应拦截器的实现，我们需要从teacherData.data中获取实际数据
    const actualData = teacherData.data || {};
    
    Object.assign(teacherInfo, {
      teacherId: actualData.teacherId || '',
      name: actualData.name || '未知',
      gender: actualData.gender || '',
      teachingGrade: actualData.teachingGrade || '',
      teachingClass: actualData.teachingClass || '',
      phone: actualData.phoneNumber || actualData.contactInfo || '', // 映射phoneNumber或contactInfo到phone
      email: actualData.email || '',
      college: actualData.college || '体育学院' // 设置默认值
    });
  } catch (error) {
    console.error('获取教师信息失败:', error);
    // 不显示错误提示，避免在退出登录过程中显示未登录信息
    // 只在控制台打印错误
    if (error.response && error.response.status === 401) {
      console.error('未登录或登录已过期');
    } else {
      console.error(`获取教师信息失败: ${error.message || '未知错误'}`);
    }
    
    // 保留基本信息显示
    teacherInfo.name = '请检查连接';
    teacherInfo.college = '体育学院';
  }
};

onMounted(() => {
  fetchTeacherInfo();
});
</script>

<style scoped>
.teacher-profile {
  padding: 20px 0;
}

.welcome-message {
  font-size: 30px;
  color: #1890ff;
  margin-top: 15px;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e8e8e8;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.edit-btn {
  margin-top: 0;
}

.profile-form {
  padding: 30px 0;
}

/* 再次增大表单行之间的间距 */
.profile-form .el-form-item {
  margin-bottom: 50px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}
</style>